import type { GenericFormType } from '@neo/blocks/forms/types'
import { cn } from '@neo/lib/utils'
import { Input } from '@neo/primitives/input'
import { ScrollArea } from '@neo/primitives/scroll-area'
import { Trash2, Upload } from 'lucide-react'
import type React from 'react'
import { VALID_FILE_SIZES_IN_MB } from 'v2/configs/constants'
import { NodevarList } from 'v2/pages/flow/components/nodevar-list'
import type { SendWhatsappNodeData } from 'v2/pages/flow/types'
import { useFileUpload } from 'v2/shared/hooks/use-file-upload'
import { ConvChannels } from 'v2/types/api/conversations'
import { type Chunk, type ChunkSender, ChunkState, ChunkType } from 'v2/types/chunk.internal'
import { errorToast } from 'v2/utils/toasts'
import z from 'zod'
import { TemplateChunkBubble } from '../../components/chunk-renderer/template-chunk-bubble'
import { getHeaderObject } from '../utils'
import type { OutboundWAFormData } from './outbound-wa-form'

export type WhatsAppFormType = OutboundWAFormData | SendWhatsappNodeData['config']

interface VariableEditorProps {
  form: GenericFormType<WhatsAppFormType>
  value: { header: string[]; body: string[] }
  nodeVars?: string[]
}

const ALLOWED_FILE_TYPES = {
  IMAGE: 'image/jpeg,image/png,image/gif,image/webp',
  VIDEO: 'video/mp4,video/webm,video/quicktime',
  DOCUMENT:
    'application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,text/plain',
} as const

const convertMBToBytes = (mbString: string): number => {
  const mb = parseInt(mbString.split(' ')[0], 10)
  return mb * 1024 * 1024 // Convert MB to bytes
}

export const VariableEditor: React.FC<VariableEditorProps> = ({ form, value, nodeVars }) => {
  const selectedTemplate = form.state.values.template
  const headerComponent = selectedTemplate?.data.components.find(
    (c: { type: string }) => c.type === 'HEADER',
  )
  const bodyComponent = selectedTemplate?.data.components.find(
    (c: { type: string }) => c.type === 'BODY',
  )
  const hasHeaderImage =
    headerComponent?.format === 'IMAGE' ||
    headerComponent?.format === 'DOCUMENT' ||
    headerComponent?.format === 'VIDEO'
  const hasHeaderText = headerComponent?.format === 'TEXT'

  const needsConfiguration = hasHeaderImage || value.header.length > 0 || value.body.length > 0

  const generateChunk = (): Chunk<ChunkType.template> => ({
    type: ChunkType.template,
    meta: {
      template: selectedTemplate,
      templateData: {
        components: [
          {
            type: 'header',
            parameters: value.header.map((_, index) =>
              getHeaderObject(headerComponent?.format || '', value.header[index]),
            ),
          },
          {
            type: 'body',
            parameters: value.body.map((_, index) => ({
              text: value.body[index],
              type: 'text',
            })),
          },
        ],
        language: { code: selectedTemplate?.data.language || '', policy: 'deterministic' },
        name: selectedTemplate?.data.name || '',
        namespace: selectedTemplate?.data.namespace || '',
        template_uuid: selectedTemplate?.uuid || '',
      },
      state: ChunkState.SENT,
      channel_meta: {
        error: null,
        error_data: null,
        replied_message: null,
        replied_to: null,
        transfer_type: null,
        status: ChunkState.SENT,
        timestamp: new Date().toISOString(),
      },
    },
    timetoken: Date.now(),
    channel: ConvChannels.WHATSAPP,
    id: 'preview',
    sender: 'system' as ChunkSender,
  })

  const previewChunk = generateChunk()

  const { uploadFile, uploadedFiles, removeFile, isUploading } = useFileUpload((file) => {
    ;(form as GenericFormType<OutboundWAFormData>).setFieldValue(
      'variables.header[0]',
      file.file_url,
    )
    ;(form as GenericFormType<OutboundWAFormData>).validateField('variables.header[0]', 'change')
  })

  const getAcceptedFileTypes = () => {
    if (!headerComponent?.format) return ''
    return ALLOWED_FILE_TYPES[headerComponent.format as keyof typeof ALLOWED_FILE_TYPES] || ''
  }

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (!file) return

    const maxSize = convertMBToBytes(VALID_FILE_SIZES_IN_MB.WHATSAPP)

    if (file.size > maxSize) {
      errorToast({
        title: 'File size too large',
        description: `File size must be less than ${VALID_FILE_SIZES_IN_MB.WHATSAPP}`,
      })
      event.target.value = '' // Reset input
      return
    }

    await uploadFile(file)
  }

  const handleRemoveFile = () => {
    removeFile(uploadedFiles[0].file_id)
    form.setFieldValue('variables.header[0]', '')
  }

  return (
    <div className="w-full h-full">
      <ScrollArea className="h-full -mr-4 pr-4">
        <div className={`flex ${needsConfiguration ? 'gap-6' : 'justify-center'}`}>
          {needsConfiguration && (
            <div className="flex-1">
              <div className="grid grid-cols-1 gap-4">
                {hasHeaderImage && (
                  <div>
                    <div className="text-s-500 mb-2 text-secondary">Header file</div>
                    <div className="border rounded-md p-4 bg-hv1">
                      {uploadedFiles.length > 0 ? (
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-secondary truncate">
                            {uploadedFiles[0].file_name}
                          </span>
                          <button
                            type="button"
                            onClick={handleRemoveFile}
                            className="text-status-danger hover:text-status-danger/80"
                            aria-label="Remove file"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      ) : (
                        <form.Field
                          name="variables.header[0]"
                          validators={{
                            onChange: z
                              .string({ message: 'URL is required' })
                              .min(1, 'URL is required'),
                          }}
                          children={(field) => (
                            <>
                              <div className="border border-dashed border-border p-4 text-center cursor-pointer mb-2 hover:border-brand/60">
                                <input
                                  type="file"
                                  id="file-upload"
                                  className="hidden"
                                  onChange={handleFileUpload}
                                  accept={getAcceptedFileTypes()}
                                  disabled={isUploading}
                                />
                                <label htmlFor="file-upload" className="cursor-pointer">
                                  <Upload className="h-6 w-6 mx-auto mb-2" />
                                  <span className="text-sm text-secondary">
                                    {isUploading
                                      ? 'Uploading...'
                                      : `Drag or click to upload ${headerComponent?.format?.toLowerCase()} (Max ${VALID_FILE_SIZES_IN_MB.WHATSAPP})`}
                                  </span>
                                </label>
                              </div>
                              <div className="text-sm text-secondary text-center mb-2">OR</div>
                              <Input
                                inputMode="url"
                                placeholder="Enter URL"
                                value={value.header[0]}
                                onChange={(e) => {
                                  field.handleChange(e.target.value)
                                }}
                                className="bg-background rounded-md"
                              />
                              {field.state.meta.errors.length > 0 && (
                                <div className="text-status-danger text-xs mt-1">
                                  {field.state.meta.errors.join(',')}
                                </div>
                              )}
                            </>
                          )}
                        />
                      )}
                    </div>
                  </div>
                )}
                {hasHeaderText && value.header.length > 0 && (
                  <div>
                    <div className="text-s-500 mb-2 text-secondary">Header Text Variables</div>
                    <div className="p-4 bg-hv1 rounded-lg">
                      {value.header.map((_, index) => (
                        <div className="flex gap-2 items-center mb-2">
                          <span className="text-s-400">{`{{${index + 1}}}`}</span>
                          <form.Field
                            name={`variables.header[${index}]`}
                            validators={{
                              onChange: z
                                .string({ message: 'Value is required' })
                                .min(1, 'Value is required'),
                            }}
                            children={(field) => (
                              <div className="flex flex-col gap-1 w-full">
                                <Input
                                  value={value.header[index]}
                                  onChange={(e) => {
                                    field.handleChange(e.target.value)
                                  }}
                                  className="bg-background rounded-md"
                                  placeholder="Enter Value"
                                  id={`variables-header-${index}`}
                                  endIcon={
                                    nodeVars ? (
                                      <NodevarList
                                        nodevars={nodeVars}
                                        className="pr-2"
                                        // inputElementSelector=""
                                        inputElementSelector={`#variables-header-${index}`}
                                      />
                                    ) : null
                                  }
                                />
                                {field.state.meta.errors.length > 0 && (
                                  <div className="text-status-danger text-xs">
                                    {field.state.meta.errors.join(',')}
                                  </div>
                                )}
                              </div>
                            )}
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                {bodyComponent && value.body.length > 0 && (
                  <div>
                    <div className="text-s-500 mb-2 text-secondary">Body Text Variables</div>
                    <div className="p-4 bg-hv1 rounded-lg">
                      {value.body.map((_, index) => (
                        <div className="flex gap-2 items-center mb-2">
                          <span className="text-s-400">{`{{${index + 1}}}`}</span>
                          <form.Field
                            name={`variables.body[${index}]`}
                            validators={{
                              onChange: z
                                .string({ message: 'Value is required' })
                                .min(1, 'Value is required'),
                            }}
                            children={(field) => (
                              <div className="flex flex-col gap-1 w-full">
                                <Input
                                  value={value.body[index]}
                                  onChange={(e) => {
                                    field.handleChange(e.target.value)
                                  }}
                                  className="bg-background rounded-md"
                                  placeholder="Enter Value"
                                  id={`variables-body-${index}`}
                                  endIcon={
                                    nodeVars ? (
                                      <NodevarList
                                        nodevars={nodeVars}
                                        className="pr-2"
                                        // inputElementSelector=""
                                        inputElementSelector={`#variables-body-${index}`}
                                      />
                                    ) : null
                                  }
                                />
                                {field.state.meta.errors.length > 0 && (
                                  <div className="text-status-danger text-xs">
                                    {field.state.meta.errors.join(',')}
                                  </div>
                                )}
                              </div>
                            )}
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
          {!nodeVars && (
            <div className={cn(needsConfiguration ? 'flex-1 max-h-[532px]' : 'mx-auto', '')}>
              {selectedTemplate && <TemplateChunkBubble chunk={previewChunk} isPreview />}
            </div>
          )}
        </div>
      </ScrollArea>
    </div>
  )
}
